// Deterministic hashing and PRNG. Everything random in the generator derives from
// (world seed, integer keys) through these functions, so any tile can be regenerated exactly.

/** 32-bit integer mix (lowbias32 by Chris Wellons). */
export function mix32(x: number): number {
  x = x >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return x >>> 0;
}

/** Hash any number of integers (plus a seed) into a uint32. Non-integers are floored. */
export function hashInts(seed: number, ...values: number[]): number {
  let h = mix32(seed ^ 0x9e3779b9);
  for (const v of values) {
    const iv = Math.floor(v);
    // Fold the full double-precision integer range in two 32-bit halves.
    const lo = iv | 0;
    const hi = Math.floor(iv / 4294967296) | 0;
    h = mix32(h ^ mix32(lo + 0x632be5ab));
    h = mix32(h ^ mix32(hi + 0x85157af5));
  }
  return h;
}

/** Hash a string to uint32 (FNV-1a then mixed). */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return mix32(h);
}

/** Uniform float in [0,1) from a hash. */
export function hash01(seed: number, ...values: number[]): number {
  return hashInts(seed, ...values) / 4294967296;
}

/** Small fast seeded PRNG (sfc32). */
export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(seed: number) {
    this.a = mix32(seed ^ 0xa5a5a5a5);
    this.b = mix32(seed + 0x3c6ef372);
    this.c = mix32(seed ^ 0x5be0cd19);
    this.d = 1;
    for (let i = 0; i < 12; i++) this.nextU32();
  }

  nextU32(): number {
    const t = (((this.a + this.b) | 0) + this.d) | 0;
    this.d = (this.d + 1) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.c = (this.c + t) | 0;
    return t >>> 0;
  }

  /** Uniform [0,1). */
  next(): number {
    return this.nextU32() / 4294967296;
  }

  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  int(minInclusive: number, maxInclusive: number): number {
    return minInclusive + Math.floor(this.next() * (maxInclusive - minInclusive + 1));
  }
}

/** Parse the seed text box: integers are used directly, anything else is hashed. */
export function parseSeed(text: string): number {
  const t = text.trim();
  if (/^-?\d+$/.test(t)) return Number(BigInt.asUintN(32, BigInt(t)));
  return hashString(t);
}
